import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  SidebarProvider,
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { LayoutDashboard, Building2, Users, Store, LogOut, ChevronRight, Search, Receipt, BarChart3 } from "lucide-react";
import { cn } from "@/lib/utils";
import { AdminCommandPalette } from "./AdminCommandPalette";
import { toast } from "@/hooks/use-toast";

const navItems = [
  { title: "Dashboard", url: "/admin", icon: LayoutDashboard, exact: true },
  { title: "Projekte", url: "/admin/projekte", icon: Building2, exact: false },
  { title: "Kunden", url: "/admin/kunden", icon: Users, exact: false },
  { title: "Anbieter", url: "/admin/anbieter", icon: Store, exact: false },
  { title: "Rechnungen", url: "/admin/rechnungen", icon: Receipt, exact: false },
  { title: "Auswertungen", url: "/admin/auswertungen", icon: BarChart3, exact: false },
];

function AdminSidebarInner({ onOpenPalette, neuCount, neuBadge, pendingCount }: { onOpenPalette: () => void; neuCount: number; neuBadge: boolean; pendingCount: number }) {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuth();

  const isActive = (url: string, exact: boolean) =>
    exact ? location.pathname === url : location.pathname.startsWith(url);

  const handleSignOut = async () => {
    await signOut();
    navigate("/auth");
  };

  return (
    <Sidebar collapsible="icon" className="border-r border-border">
      {/* Logo / Brand */}
      <div className={cn(
        "flex items-center gap-2 px-4 py-5 border-b border-border transition-all",
        collapsed ? "justify-center px-2" : ""
      )}>
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold text-sm">
          L
        </div>
        {!collapsed && (
          <span className="font-semibold text-sm tracking-tight">LiftBlick Admin</span>
        )}
      </div>

      <SidebarContent className="flex flex-col h-full">
        {/* Search button */}
        <div className={cn("px-3 pt-3 pb-1", collapsed ? "px-2" : "")}>
          <Button
            variant="ghost"
            onClick={onOpenPalette}
            className={cn(
              "h-9 w-full flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 text-sm text-muted-foreground hover:bg-muted/80 hover:text-foreground transition-colors",
              collapsed ? "justify-center px-0 border-0 bg-transparent hover:bg-muted/50" : ""
            )}
          >
            <Search className="h-4 w-4 shrink-0" />
            {!collapsed && (
              <>
                <span className="flex-1 text-left text-xs">Suchen…</span>
                <kbd className="pointer-events-none hidden h-5 select-none items-center gap-1 rounded border border-border bg-background px-1.5 font-mono text-[10px] font-medium opacity-60 sm:flex">
                  ⌘K
                </kbd>
              </>
            )}
          </Button>
        </div>

        <SidebarGroup className="flex-1">
          {!collapsed && (
            <SidebarGroupLabel className="text-xs text-muted-foreground px-4 pt-3 pb-1">
              Navigation
            </SidebarGroupLabel>
          )}
          <SidebarGroupContent className="pt-1">
            <SidebarMenu>
              {navItems.map((item) => {
                const active = isActive(item.url, item.exact);
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      isActive={active}
                      tooltip={collapsed ? item.title : undefined}
                    >
                      <button
                        onClick={() => navigate(item.url)}
                          className={cn(
                            "relative w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                            active
                              ? "text-foreground font-medium"
                              : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                          )}
                      >
                        <item.icon className="h-4 w-4 shrink-0" />
                        {!collapsed && (
                          <>
                            <span className="flex-1 text-left">{item.title}</span>
                            {item.url === "/admin/projekte" && pendingCount > 0 && (
                              <span title="Zur Freigabe" className="inline-flex items-center justify-center h-4 min-w-4 px-1 rounded-full border border-foreground/40 text-foreground text-[10px] font-bold leading-none">
                                {pendingCount > 99 ? "99+" : pendingCount}
                              </span>
                            )}
                            {item.url === "/admin/projekte" && neuBadge && (
                              <span className="inline-flex items-center justify-center h-4 min-w-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold leading-none">
                                {neuCount > 99 ? "99+" : neuCount}
                              </span>
                            )}
                            {active && !(item.url === "/admin/projekte" && neuBadge) && (
                              <ChevronRight className="h-3 w-3 opacity-50" />
                            )}
                          </>
                        )}
                        {collapsed && item.url === "/admin/projekte" && neuBadge && (
                          <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-destructive" />
                        )}
                      </button>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Sign out at bottom */}
        <div className={cn("p-3 border-t border-border", collapsed ? "flex justify-center" : "")}>
          <Button
            variant="ghost"
            size={collapsed ? "icon" : "sm"}
            onClick={handleSignOut}
            className="text-muted-foreground hover:text-foreground w-full justify-start gap-2"
          >
            <LogOut className="h-4 w-4 shrink-0" />
            {!collapsed && <span>Abmelden</span>}
          </Button>
        </div>
      </SidebarContent>
    </Sidebar>
  );
}

interface AdminLayoutProps {
  children: React.ReactNode;
}

export function AdminLayout({ children }: AdminLayoutProps) {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [neuCount, setNeuCount] = useState(0);
  const [newestNeu, setNewestNeu] = useState<string | null>(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [lastSeen, setLastSeen] = useState<string | null>(() => {
    try { return localStorage.getItem("admin_projekte_last_seen"); } catch { return null; }
  });
  const lastSeenRef = useRef<string | null>(lastSeen);
  const location = useLocation();
  const { user, isAdmin } = useAuth();

  const markSeen = () => {
    const now = new Date().toISOString();
    lastSeenRef.current = now;
    try { localStorage.setItem("admin_projekte_last_seen", now); } catch { /* ignore */ }
    setLastSeen(now);
  };

  // Einmal angesehen: Badge bleibt weg, bis eine neue Anfrage eintrifft.
  useEffect(() => {
    if (location.pathname.startsWith("/admin/projekte")) markSeen();
  }, [location.pathname]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", handler);
    const openSearch = () => setPaletteOpen(true);
    window.addEventListener("open-admin-search", openSearch);
    return () => {
      window.removeEventListener("keydown", handler);
      window.removeEventListener("open-admin-search", openSearch);
    };
  }, []);

  // Fetch 'neu' project count for sidebar badge + realtime updates
  useEffect(() => {
    if (!user || !isAdmin) return;

    const fetchCount = () =>
      supabase
        .from("projekte")
        .select("id", { count: "exact", head: true })
        .eq("status", "neu")
        .is("geloescht_am", null)
        .then(({ count }) => setNeuCount(count ?? 0))
        .then(() =>
          supabase
            .from("projekte")
            .select("erstellt_am")
            .eq("status", "neu")
            .is("geloescht_am", null)
            .order("erstellt_am", { ascending: false })
            .limit(1)
            .maybeSingle()
            .then(({ data }) => {
              setNewestNeu(data?.erstellt_am ?? null);
              // Wer die Projekte gerade offen hat, hat alles Gesehene gesehen.
              if (data?.erstellt_am && window.location.pathname.startsWith("/admin/projekte")) markSeen();
            })
        )
        .then(() =>
          supabase
            .from("projekte")
            .select("id", { count: "exact", head: true })
            .eq("freigabe", "ausstehend")
            .is("geloescht_am", null)
            .then(({ count }) => setPendingCount(count ?? 0))
        );

    fetchCount();

    const channel = supabase
      .channel("projekte-neu-count")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "projekte" },
        (payload) => {
          fetchCount();
          const p = payload.new as { standort_ort?: string; lift_typ?: string };
          toast({
            title: "Neue Projektanfrage eingegangen",
            description: [p.lift_typ, p.standort_ort].filter(Boolean).join(" · ") || "Ein neues Projekt wartet auf Bearbeitung.",
          });
        }
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "projekte" },
        () => fetchCount()
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user, isAdmin]);

  // Badge nur leuchten lassen, wenn es eine Anfrage gibt, die nach dem letzten
  // Besuch der Projektseite neu eingegangen ist.
  const neuBadge =
    neuCount > 0 &&
    (!lastSeen || !newestNeu || new Date(newestNeu).getTime() > new Date(lastSeen).getTime());

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <AdminSidebarInner onOpenPalette={() => setPaletteOpen(true)} neuCount={neuCount} neuBadge={neuBadge} pendingCount={pendingCount} />

        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-12 flex items-center gap-3 border-b border-border bg-background/95 px-3 sm:px-4 sticky top-0 z-20 backdrop-blur-sm">
            <SidebarTrigger className="text-muted-foreground hover:text-foreground" />
            <div className="ml-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPaletteOpen(true)}
                className="h-8 gap-2 bg-muted/40 text-muted-foreground"
              >
                <Search className="h-3.5 w-3.5" />
                <span className="hidden sm:inline text-xs">Suchen…</span>
                <kbd className="pointer-events-none hidden h-5 select-none items-center gap-1 rounded border border-border bg-background px-1.5 font-mono text-[10px] font-medium opacity-60 sm:flex">
                  ⌘K
                </kbd>
              </Button>
            </div>
          </header>

          <main className="flex-1 overflow-auto">
            {children}
          </main>
        </div>
      </div>

      <AdminCommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </SidebarProvider>
  );
}
